import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Box, Card, CardContent, TextField, Button, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { authAPI } from '../services/api';

// Where each role lands after signing in.
export function homeFor(role) {
  if (role === 'admin') return '/admin';
  if (role === 'viewer') return '/viewer';
  if (role === 'facilitator') return '/facilitator';
  return '/reviewer';
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [userId2FA, setUserId2FA] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const auth = useAuth();
  const branding = useBranding();
  const { login } = auth;
  const navigate = useNavigate();

  if (auth.user && !auth.loading) {
    return <Navigate to={homeFor(auth.user.role)} replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      // Check if login returned requires_2fa flag
      if ((user && user.requires_2fa)) {
        setNeeds2FA(true);
        setUserId2FA(user.user_id);
        setLoading(false);
        return;
      }
      navigate(homeFor(user.role), { replace: true });
    } catch (err) {
      const data = (err.response && err.response.data);
      if ((data && data.data && data.data.requires_2fa)) {
        setNeeds2FA(true);
        setUserId2FA(data.data.user_id);
      } else {
        setError((data && data.error) || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.verify2FA(userId2FA, totpCode);
      const { token, user } = res.data.data;
      localStorage.setItem('okc_token', token);
      localStorage.setItem('okc_user', JSON.stringify(user));
      window.location.replace(homeFor(user.role));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
               background: 'linear-gradient(135deg, ' + branding.website_primary + ' 0%, ' + (branding.website_secondary || '#2980b9') + ' 100%)' }}>
      <Card sx={{ maxWidth: 420, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 0.5, color: 'primary.main' }}>{branding.portal_name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {needs2FA ? 'Enter your authenticator code' : branding.portal_tagline + ' — Sign in to continue'}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!needs2FA ? (
            <form onSubmit={handleLogin}>
              <TextField fullWidth label="Email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} required autoFocus />
              <TextField fullWidth label="Password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} sx={{ mb: 3 }} required />
              <Button fullWidth variant="contained" type="submit" size="large" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2FA}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Open Google Authenticator and enter the 6-digit code for OKC2027
              </Typography>
              <TextField fullWidth label="6-digit code" value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
                sx={{ mb: 3 }} autoFocus />
              <Button fullWidth variant="contained" type="submit" size="large"
                disabled={loading || totpCode.length < 6}>
                {loading ? 'Verifying…' : 'Verify'}
              </Button>
              <Button fullWidth sx={{ mt: 1 }} onClick={() => { setNeeds2FA(false); setTotpCode(''); }}>
                Back to login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
